import { Typography } from '@mui/material';
import { Grid, TextField, Button, FormControlLabel, Checkbox, Skeleton, Box, Stack, Switch } from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from './../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { useEffect, useState } from 'react';
import { setDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  overSpeedAlert: {
    speed_threshold: 0,
    enable: false,
    submit: null
  }
};

const OverSpeedAlertSettingsPage = () => {
  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [overSpeedAlertSettings, setOverSpeedAlertSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 5 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    // console.log(selectedDevice)
    if (!selectedDevice) return;
    setLoading(true);
    (async () => {
      await FirestoreDataService.getDeviceSettings(selectedDevice)
        .then((documentSnap) => {
          if (documentSnap.exists()) {
            let overSpeedAlertObject = documentSnap.data().over_speed_alert;
            // console.log('Fetched:-', overSpeedAlertObject);
            setOverSpeedAlertSettings(overSpeedAlertObject);
          }
        })
        .catch((e) => {
          const msg = 'Failed to get over speed alert settings!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
          console.info(msg, e);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [selectedDevice]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary">
          Over Speed Alert Settings
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" height={230} />
          </>
        ) : (
          <Formik
            initialValues={overSpeedAlertSettings ? overSpeedAlertSettings : INITIAL_VALUES}
            validationSchema={Yup.object().shape({
              overSpeedAlert: Yup.object()
                .shape({
                  speed_threshold: Yup.number()
                    .min(20, 'Time must be greater than or equal to 10')
                    .max(400, 'Time must be less than or equal to 3600'),
                  enable: Yup.boolean()
                })
                .required()
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                setStatus({ success: false });
                setSubmitting(true);

                // Check permission: Try Redux appUser first, fallback to sessionStorage for partner sessions
                let canModifySettings = appUser?.organization?.extra_info?.can_user_modify_settings;
                if (!canModifySettings) {
                  const isPartnerSession = typeof window !== 'undefined' && sessionStorage.getItem('is_partner_session') === 'true';
                  if (isPartnerSession) {
                    try {
                      const userDataStr = sessionStorage.getItem('user');
                      if (userDataStr) {
                        const partnerUser = JSON.parse(userDataStr);
                        canModifySettings = partnerUser?.organization?.extra_info?.can_user_modify_settings;
                      }
                    } catch (e) {
                      console.error('Failed to parse partner user data from sessionStorage:', e);
                    }
                  }
                }
                if (!canModifySettings) {
                  dispatch(
                    showAppAlert({
                      show: true,
                      alertType: 'error',
                      alertMessage: 'Error: You do not have permission to save these settings.'
                    })
                  );
                  return;
                }

                let payload = { ...values };
                // console.log('payload', payload);

                if (values.speed_threshold < 20) {
                  payload = { ...values, speed_threshold: 20 };
                }

                if (values.angle_threshold > 400) {
                  payload = { ...values, speed_threshold: 400 };
                }

                const tasksRef = doc(db, 'tasks', selectedDevice);
                setDoc(tasksRef, {
                  device_id: selectedDevice,
                  type: 'over_speed_alert',
                  user_id: appUser.email ? appUser.email : '-',
                  old_value: overSpeedAlertSettings,
                  payload
                })
                  .then((response) => {
                    // console.log('saved tasks!', response);
                    const msg = 'Over speed alert settings saved successfully !';
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    const msg = 'Failed to save over speed alert settings!';
                    console.log(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  });
              } catch (err) {
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save Over Speed Alert Settings' }));
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ mt: 2 }}>
                  <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex' }}>
                    <Typography variant="caption" color="textSecondary">
                      Threshold Settings for Over Speed Alert
                    </Typography>
                    <FormControlLabel
                      name="enable"
                      id="enable"
                      control={<Switch onChange={handleChange} checked={values.enable} />}
                      label="Enable"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Stack spacing={1} style={{ marginTop: '16px' }}>
                      <TextField
                        disabled={!values.enable}
                        fullWidth
                        id="speed_threshold"
                        type="number"
                        value={values.speed_threshold}
                        name="speed_threshold"
                        onChange={handleChange}
                        placeholder="20 - 400 Unit km/h"
                        label="Speed Threshold"
                      />
                    </Stack>
                  </Grid>
                  <Button
                    disableElevation
                    style={{ float: 'right', marginTop: 16, marginBottom: 16 }}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        )}
      </MainCard>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </Grid>
  );
};

export default OverSpeedAlertSettingsPage;
